/**
 * SoundComponent - audio playback controller for actors. Manages loading and playback
 * of audio clips as either positional (3D spatial) or global sounds. Integrates with
 * the world's AudioListener for proper 3D audio positioning.
 */
import * as THREE from 'three';

import { AssetPath } from '../../storage/storageProvider.js';
import { EngineClass } from '../../systems/ClassRegistry.js';
import { GlobalAudioManager } from '../../utils/GlobalAudioManager.js';
import { AsyncTaskWorker, IAsyncLoadableTag } from '../../utils/IAsyncLoadable.js';
import { resourceManager } from '../../utils/ResourceManager.js';
import { property } from '../../utils/serialization/decorator.js';
import { SceneComponent } from '../SceneComponent.js';

import { SoundResource } from './SoundResource.js';

import type { IAsyncLoadable } from '../../utils/IAsyncLoadable.js';
import type { SceneComponentOptions } from '../SceneComponent.js';

export interface SoundComponentOptions extends SceneComponentOptions {
  /** Whether sounds should loop by default */
  loop?: boolean;
  /** Whether sounds should be positional (3D) or global */
  positional?: boolean;
  /** Whether to automatically play the first sound on component start */
  autoPlay?: boolean;
  /** The key of the sound resource to autoplay (defaults to first sound resource if undefined) */
  autoPlayClipKey?: string;
  /** Audio bus to use (defaults to 'SFX') */
  bus?: string;
  /** List of Sound resources */
  sounds?: Array<SoundResource>;
}

@EngineClass('SoundComponent')
export class SoundComponent extends SceneComponent implements IAsyncLoadable {
  readonly [IAsyncLoadableTag] = true;

  // TODO LRK: Metadata generation isn't filtering for child type correctly.
  // It's serializing as string, but needs to be manually loaded then in begin play.
  @property({ type: 'array', child: { type: 'resource', resourceType: 'SoundResource'} })
  private soundResources: Array<SoundResource> = [];

  @property()
  private loop: boolean = false;

  @property()
  private positional: boolean = false;

  @property()
  private autoPlay: boolean = false;

  @property()
  private autoPlayClipKey: string | undefined;

  @property()
  private bus: string = 'SFX';

  public onEnded?: () => void;
  private playing: boolean = false;
  private currentSound: THREE.Audio | THREE.PositionalAudio | null = null;
  private sounds: Map<string, THREE.Audio | THREE.PositionalAudio> = new Map();
  private audioBuffers: Map<string, AudioBuffer> = new Map();
  private _asyncTaskWorker: AsyncTaskWorker = new AsyncTaskWorker();


  public override initialize(options?: SoundComponentOptions): void {
    super.initialize(options);

    this.loop = options?.loop ?? this.loop;
    this.positional = options?.positional ?? this.positional;
    this.autoPlayClipKey = options?.autoPlayClipKey ?? this.autoPlayClipKey;
    this.bus = options?.bus ?? this.bus;
    this.soundResources = options?.sounds ?? this.soundResources;
    this.autoPlay = options?.autoPlay ?? this.autoPlay;
  }



  private getAudioListener(): THREE.AudioListener | null {
    // Get the world from the actor's owner
    const world = this.getActor()?.getWorld();
    if (!world) return null;

    return world.audioListener;
  }

  private getGlobalAudioManager(): GlobalAudioManager {
    const world = this.getActor()?.getWorld();
    if (world && (world as any).globalAudioManager) {
      return (world as any).globalAudioManager;
    }
    // Fallback to singleton if world is not available (though component should always have world when playing)
    return GlobalAudioManager.getInstance();
  }



  // start IAsyncLoadable
  public async waitForLoad(): Promise<void> {
    await this._asyncTaskWorker.waitUntilComplete();
  }

  public isLoading(): boolean {
    return this._asyncTaskWorker.isRunning();
  }
  // end IAsyncLoadable

  public override beginPlay(): void {
    super.beginPlay();

    this._asyncTaskWorker.runTask(async () => {
      const loadedResources: SoundResource[] = [];

      for (let i = 0; i < this.soundResources.length; i++) {
        const sr = this.soundResources[i];
        if (typeof sr === 'string') {
          const loaded = await resourceManager.loadResource(AssetPath.fromString(sr as string));
          if (loaded && (loaded as any).instance) {
            loadedResources.push((loaded as any).instance as SoundResource);
          }
        } else if (sr instanceof SoundResource) {
          loadedResources.push(sr);
        }
      }

      this.soundResources = loadedResources;

      // Load all underlying audio buffers for the resolved sound resources
      const resourcePromises = this.soundResources.map((sound) => this.loadSoundResource(sound));
      await Promise.all(resourcePromises);

      // Autoplay if configured
      if (this.autoPlay && this.soundResources.length > 0) {
        let clipKeyToPlay = this.soundResources[0].name; // Default to first sound

        // If a specific key was requested, try to find it
        if (this.autoPlayClipKey) {
          const targetResource = this.soundResources.find(s => s.name === this.autoPlayClipKey);
          if (targetResource) {
            clipKeyToPlay = targetResource.name;
          } else {
            console.warn(`SoundComponent: autoPlayClipKey '${this.autoPlayClipKey}' not found. Falling back to '${clipKeyToPlay}'.`);
          }
        }

        this.play(clipKeyToPlay);
      }
    });
  }

  private async loadSoundResource(sound: SoundResource): Promise<void> {
    try {
      if (!sound.audioPath) return;
      const buffer = await resourceManager.loadSound(AssetPath.fromString(sound.audioPath));
      if (!buffer) {
        console.error(`Failed to load Sound resource ${sound.name} from ${sound.audioPath}`);
        return;
      }

      this.audioBuffers.set(sound.name, buffer);

      let soundObject: THREE.Audio<any> | THREE.PositionalAudio;
      const listener = this.getAudioListener();
      if (!listener) return;

      if (this.positional) {
        const posSound = new THREE.PositionalAudio(listener);
        if (sound.maxDistance !== undefined) posSound.setMaxDistance(sound.maxDistance);
        if (sound.distanceModel !== undefined) posSound.setDistanceModel(sound.distanceModel);
        if (sound.rolloffFactor !== undefined) posSound.setRolloffFactor(sound.rolloffFactor);
        soundObject = posSound;
      } else {
        soundObject = new THREE.Audio(listener);
      }

      // Re-route the underlying WebAudio graph to our bus if one exists.
      // By default THREE connects soundObject.gain -> listener.getInput() in the constructor.
      // We sever that connection and route it to our bus instead, preserving spatial tracking.
      const bus = this.getGlobalAudioManager().getBus(this.bus);
      if (bus && (soundObject as any).gain) {
        const gainNode = (soundObject as any).gain as GainNode;
        gainNode.disconnect();
        gainNode.connect(bus.getInput());
      }

      if (sound.volume !== undefined) soundObject.setVolume(sound.volume);
      soundObject.setLoop(this.loop);
      soundObject.setBuffer(buffer);

      this.add(soundObject);
      this.sounds.set(sound.name, soundObject);

    } catch (error) {
      console.error(`Failed to load Sound resource ${sound.name} from ${sound.audioPath}:`, error);
    }
  }

  public override endPlay(): void {
    // Stop all sounds
    this.stopAll();

    super.endPlay();
  }

  /**
   * Play a sound by its key.
   * @param key - The key of the sound to play
   * @param volume - Optional volume override
   * @param forceRestart - Whether to force restart even if already playing
   * @returns True if the sound was played successfully
   */
  public async play(key: string, volume?: number, forceRestart: boolean = false): Promise<boolean> {
    // Guard: don't start a new sound if one is already playing
    if (!forceRestart && this.playing && this.currentSound && this.currentSound.isPlaying) {
      return false;
    }
    const sound = this.sounds.get(key);
    if (!sound) {
      console.warn(`Sound with key "${key}" not found`);
      return false;
    }

    // Stop the current sound if any
    if (this.currentSound && this.currentSound.isPlaying) {
      this.currentSound.stop();
    }

    // Apply overrides if provided
    if (volume !== undefined) {
      sound.setVolume(volume);
    }

    // Add ended event listener
    if (sound.source && sound.source instanceof AudioBufferSourceNode) {
      sound.source.onended = () => {
        this.playing = false;
        if (this.onEnded) this.onEnded();
      };
    } else if ((sound as any).onEnded) {
      (sound as any).onEnded = () => {
        this.playing = false;
        if (this.onEnded) this.onEnded();
      };
    }

    // Resume AudioContext if suspended
    const listener = this.getAudioListener();
    if (listener && listener.context.state === 'suspended') {
      try {
        await listener.context.resume();
      } catch (err) {
        console.warn('Browser blocked AudioContext resume without user gesture. Sound will start once user interacts.');
      }

      if (listener.context.state === 'suspended') {
        console.warn(`AudioContext is suspended. Auto-queued sound "${key}" to play upon next user interaction.`);
        // We do NOT return false here.
        // Calling sound.play() while context is suspended will correctly queue the node to start once the user clicks.
      }
    }

    // Play the sound
    try {
      sound.play();
      this.currentSound = sound;
      this.playing = true;
      return true;
    } catch (e) {
      console.error(`Error playing sound "${key}":`, e);
      return false;
    }
  }

  public stop(): void {
    if (this.currentSound) {
      this.currentSound.stop();
      this.playing = false;
      if (this.onEnded) this.onEnded();
      this.currentSound = null;
    }
  }

  public stopAll(): void {
    this.sounds.forEach(sound => {
      if (sound.isPlaying) {
        sound.stop();
      }
    });

    this.playing = false;
    this.currentSound = null;
  }

  public pause(): void {
    if (this.currentSound && this.currentSound.isPlaying) {
      this.currentSound.pause();
      this.playing = false;
    }
  }

  public resume(): void {
    if (this.currentSound && !this.currentSound.isPlaying) {
      this.currentSound.play();
      this.playing = true;
    }
  }

  public isPlaying(): boolean {
    return this.playing;
  }

  /**
   * Set the volume for a specific sound.
   * @param key - The key of the sound
   * @param volume - The volume (0.0 to 1.0)
   */
  public setVolume(key: string, volume: number): void {
    const sound = this.sounds.get(key);
    if (sound) {
      sound.setVolume(Math.max(0, Math.min(1, volume)));
    }
  }

  /**
   * Set the volume for all sounds.
   * @param volume - The volume (0.0 to 1.0)
   */
  public setVolumeAll(volume: number): void {
    const clampedVolume = Math.max(0, Math.min(1, volume));
    this.sounds.forEach(sound => {
      sound.setVolume(clampedVolume);
    });
  }

  /**
   * Set whether a sound should loop.
   * @param key - The key of the sound
   * @param loop - Whether the sound should loop
   */
  public setLoop(key: string, loop: boolean): void {
    const sound = this.sounds.get(key);
    if (sound) {
      sound.setLoop(loop);
    }
  }

  /**
   * Get the underlying THREE.Audio or THREE.PositionalAudio instance for a sound.
   * Useful for direct WebAudio API manipulation. Note: Respects the bus system as the
   * original listener connection was rerouted to the correct bus input.
   * @param key - The key of the sound
   * @returns The THREE.Audio instance, or undefined if not found
   */
  public getAudio(key: string): THREE.Audio | THREE.PositionalAudio | undefined {
    return this.sounds.get(key);
  }

  /** Get the underlying AudioContext for creating WebAudio nodes (filters, analyzers, etc.) */
  public getAudioContext(): AudioContext | undefined {
    return this.getAudioListener()?.context;
  }

  /**
   * Apply a single WebAudio filter node to a specific sound.
   * @param key - The key of the sound
   * @param filter - The WebAudio node to apply
   */
  public setFilter(key: string, filter: AudioNode): void {
    const sound = this.sounds.get(key);
    if (sound) {
      sound.setFilter(filter as any);
    }
  }

  /**
   * Apply an array of WebAudio filter nodes to a specific sound.
   * Nodes are connected in the order provided.
   * @param key - The key of the sound
   * @param filters - WebAudio nodes to apply
   */
  public setFilters(key: string, filters: AudioNode[]): void {
    const sound = this.sounds.get(key);
    if (sound) {
      sound.setFilters(filters as any[]);
    }
  }

  /**
   * Get the current array of WebAudio filter nodes applied to a specific sound.
   * @param key - The key of the sound
   */
  public getFilters(key: string): AudioNode[] {
    const sound = this.sounds.get(key);
    return sound ? sound.getFilters() : [];
  }


  public override getEditorClassIcon(): string | null {
    return 'Icon_Sound';
  }
}
